import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Loader2, ShieldCheck, Sparkles, Clock3 } from 'lucide-react';
import { supabase, getErrorMessage, isMockMode, isSupabaseMisconfigured, supabaseConfigErrorMessage } from '../services/supabaseClient';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { useAuth } from '../App';

const AUTH_BRAND_NAME = 'PCNGuard Fleet';
const AUTH_LOGO_SRC = '/logo.svg';

const INPUT_CLASS =
  'w-full h-11 px-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-400';
const PRIMARY_BUTTON_CLASS =
  'w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-colors flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed';
const LABEL_CLASS = 'block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600 mb-1.5';

const shellStyle: React.CSSProperties = {
  fontFamily: '"Avenir Next", "Segoe UI", Tahoma, sans-serif',
};

const AuthShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen relative overflow-hidden bg-slate-950" style={shellStyle}>
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-28 -left-24 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-cyan-400/15 blur-3xl" />
      <div className="absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-slate-500/20 blur-3xl" />
    </div>
    <div className="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">{children}</div>
  </div>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authError, clearAuthError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isForgotOpen, setIsForgotOpen] = useState(false);

  const params = new URLSearchParams(location.search);
  const onboarding = params.get('onboarding') === '1';
  const next = params.get('next');
  const postAuthPath = next || (onboarding ? '/?onboarding=1' : '/dashboard');

  useEffect(() => {
    if (!authError) return;
    setError(authError);
  }, [authError]);

  useEffect(() => {
    if (!isSupabaseMisconfigured) return;
    setError(supabaseConfigErrorMessage);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAuthError();
    setLoading(true);
    setError(null);

    if (isSupabaseMisconfigured) {
      setLoading(false);
      setError(supabaseConfigErrorMessage);
      return;
    }

    if (isMockMode) {
      setTimeout(() => {
        setLoading(false);
        navigate(postAuthPath);
      }, 800);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      if (data.user) {
        const { data: profile } = await supabase.from('profiles').select('id').eq('id', data.user.id).limit(1).maybeSingle();

        if (!profile) {
          await supabase.auth.signOut();
          setError('Account setup incomplete. Please sign up first.');
          setLoading(false);
          return;
        }

        navigate(postAuthPath);
      }
    } catch (err: any) {
      console.error(err);
      const msg = getErrorMessage(err);
      if (msg === 'Failed to fetch') {
        setError('Could not connect to the database. Please check your internet connection or Supabase configuration.');
      } else {
        setError(msg);
      }
    } finally {
      if (!isMockMode) setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    clearAuthError();

    if (isSupabaseMisconfigured) {
      setError(supabaseConfigErrorMessage);
      return;
    }

    if (isMockMode) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        navigate(postAuthPath);
      }, 1000);
      return;
    }

    try {
      setLoading(true);
      const redirectTo = `${window.location.origin}/#${postAuthPath}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      });

      if (error) throw error;
    } catch (err: any) {
      console.error(err);
      setLoading(false);
      setError(getErrorMessage(err));
    }
  };

  return (
    <AuthShell>
      <div className="w-full max-w-5xl grid items-stretch gap-5 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6">
        <section className="rounded-3xl border border-white/15 bg-white/10 p-7 text-slate-100 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white shadow-md overflow-hidden flex items-center justify-center">
              <img
                src={AUTH_LOGO_SRC}
                alt={`${AUTH_BRAND_NAME} logo`}
                className="h-[132%] w-[132%] max-w-none object-contain"
                loading="eager"
                decoding="sync"
              />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-200">Secure Access</p>
              <h1 className="text-2xl font-bold text-white">{AUTH_BRAND_NAME}</h1>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-3xl font-bold leading-tight text-white">Sign in to your fleet control center</h2>
            <p className="mt-3 text-sm leading-6 text-slate-200">
              Track liabilities, prioritize actions, and keep your team aligned on every ticket lifecycle stage.
            </p>
          </div>

          <div className="mt-8 grid gap-3">
            <div className="rounded-2xl border border-white/15 bg-slate-900/35 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-sm font-semibold">Secure Session Controls</span>
              </div>
              <p className="mt-1 text-xs text-slate-200">Role-based access and verification checks on every sign in.</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-slate-900/35 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-200">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold">Priority Workflow Context</span>
              </div>
              <p className="mt-1 text-xs text-slate-200">Jump back into live operations with decision-ready dashboards.</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-slate-900/35 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-200">
                <Clock3 className="h-4 w-4" />
                <span className="text-sm font-semibold">Deadline Discipline</span>
              </div>
              <p className="mt-1 text-xs text-slate-200">Keep appeal windows and payment milestones under control.</p>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-7 shadow-2xl">
          <div className="mb-6 text-center">
            <h3 className="text-2xl font-bold text-slate-900">Welcome back</h3>
            <p className="mt-2 text-sm text-slate-600">
              {isSupabaseMisconfigured
                ? 'Authentication is temporarily unavailable'
                : isMockMode
                  ? 'Demo mode active'
                  : 'Sign in to continue managing your fleet PCNs'}
            </p>
          </div>

          {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className={LABEL_CLASS}>Email Address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  clearAuthError();
                  setEmail(e.target.value);
                }}
                className={INPUT_CLASS}
                placeholder="name@company.com"
              />
            </div>

            <div>
              <label className={LABEL_CLASS}>Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => {
                  clearAuthError();
                  setPassword(e.target.value);
                }}
                className={INPUT_CLASS}
                placeholder="********"
              />
            </div>

            <button type="submit" disabled={loading || isSupabaseMisconfigured} className={`${PRIMARY_BUTTON_CLASS} mt-2`}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">or continue with</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading || isSupabaseMisconfigured}
            className="w-full h-11 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign in with Google
          </button>

          <div className="mt-6 text-center text-sm text-slate-600 space-y-2">
            <div>
              <button type="button" onClick={() => setIsForgotOpen(true)} className="font-semibold text-slate-900 hover:text-slate-700">
                Forgot your password?
              </button>
            </div>
            <div>
              Don&apos;t have an account?{' '}
              <Link to={`/signup${location.search || ''}`} className="font-semibold text-slate-900 hover:text-slate-700">
                Create one
              </Link>
            </div>
          </div>
        </section>
      </div>

      <ForgotPasswordModal isOpen={isForgotOpen} onClose={() => setIsForgotOpen(false)} />
    </AuthShell>
  );
};

export default Login;
